if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      // Read the installation add-on variant id
      getInstallationVariantId() {
        const section = document.getElementById(`MainProduct-${this.dataset.sectionId}`);
        const serviceRoot = section?.querySelector('[data-installation-service]');
        if (!serviceRoot) return null;

        const variantId = Number(serviceRoot.dataset.installationVariantId);
        return variantId || null;
      }

      getInstallationExtraHours() {
        const section = document.getElementById(`MainProduct-${this.dataset.sectionId}`);
        const serviceRoot = section?.querySelector('[data-installation-service]');
        if (!serviceRoot) return null;

        const quantityInput = serviceRoot?.querySelector('[data-installation-extra-hours-quantity]');
        const quantity = Number(quantityInput?.value || 0);
        if (!quantity) return null;

        const variantId = Number(serviceRoot?.dataset.installationExtraHoursVariantId);
        if (!variantId) return null;

        return { variantId, quantity };
      }

      getInstallationDate() {
        const section = document.getElementById(`MainProduct-${this.dataset.sectionId}`);
        const serviceRoot = section?.querySelector('[data-installation-service]');
        const input = serviceRoot?.querySelector('[data-installation-date-input]');
        const value = input?.value?.trim();
        return value || null;
      }

      getProductSku(variantId) {
        if (!variantId) return null;

        const variantSelects = document.getElementById(`variant-selects-${this.dataset.sectionId}`);
        const variantJson = variantSelects?.querySelector('script[type="application/json"]')?.textContent;

        if (variantJson) {
          try {
            const variants = JSON.parse(variantJson);
            const match = variants.find((variant) => Number(variant.id) === Number(variantId));
            if (match?.sku) return match.sku;
          } catch {}
        }

        return this.dataset.productSku || null;
      }

      buildLineItem(formData) {
        const properties = {};
        for (const [key, value] of formData) {
          if (key.startsWith('properties[') && key.endsWith(']')) {
            properties[key.slice('properties['.length, -1)] = value;
          }
        }

        const item = {
          id: Number(formData.get('id')),
          quantity: Number(formData.get('quantity') || 1),
        };
        if (Object.keys(properties).length) item.properties = properties;
        return item;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const formData = new FormData(this.form);
        const primaryItem = this.buildLineItem(formData);
        const installationVariantId = this.getInstallationVariantId();
        const extraHours = this.getInstallationExtraHours();
        const isInstallationProductPage = this.dataset.isInstallationProductPage === 'true';
        const primaryVariantId = primaryItem.id || formData.get('id');
        const isInstallationOnly =
          isInstallationProductPage ||
          (installationVariantId && Number(primaryVariantId) === Number(installationVariantId));
        const sections = this.cart?.getSectionsToRender().map((section) => section.id);

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        if (installationVariantId) {
          const baseQuantity = Number(primaryItem.quantity) || 1;
          const installationItem = {
            id: installationVariantId,
            quantity: baseQuantity,
          };

          const productTitle = this.dataset.productTitle;
          const productSku = this.getProductSku(primaryVariantId);
          const installationDate = this.getInstallationDate();
          const installationProperties = {};
          if (productTitle && !isInstallationOnly) installationProperties['Installation for'] = productTitle;
          if (productSku && !isInstallationOnly) installationProperties['Product SKU'] = productSku;
          if (installationDate) installationProperties['Installation date'] = installationDate;
          if (Object.keys(installationProperties).length) installationItem.properties = installationProperties;

          const items = isInstallationOnly ? [installationItem] : [primaryItem, installationItem];
          if (extraHours?.variantId) {
            const extraHoursQuantity = Number(extraHours.quantity) * baseQuantity;
            if (extraHoursQuantity > 0) {
              const extraHoursItem = {
                id: extraHours.variantId,
                quantity: extraHoursQuantity,
              };
              if (Object.keys(installationProperties).length) extraHoursItem.properties = installationProperties;
              items.push(extraHoursItem);
            }
          }

          const body = { items };

          if (sections) {
            body.sections = sections;
            body.sections_url = window.location.pathname;
            this.cart.setActiveElement(document.activeElement);
          }

          config.body = JSON.stringify(body);
        } else {
          // Fall back to the standard FormData submit when no add-on is selected.
          delete config.headers['Content-Type'];
          if (sections) {
            formData.append('sections', sections);
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: primaryVariantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error && response.items && !response.key) response.key = response.items[0]?.key;
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: primaryVariantId,
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
